import { deserialize, Binary } from 'bson';

import { fetch } from '@common/react/components/Api/Fetch';

import { BaseUser } from '@common/typescript/objects/BaseUser';
import { BaseApplicationState } from '@common/react/store';
import { BaseParams } from '@common/typescript/objects/BaseParams';
import { ResponseErrorContent, ResponseError } from '@common/react/components/Api/RequestError';

interface Message<T> {
	success: number;
	response: T;
	session: string;
}

enum ApplicationMediaType {
	json = 'application/json',
	bson = 'application/bson',
}

function getContentType(response: Response): string {
	const header = response.headers.get('content-type');

	return header ?? '';
}

function isObject(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeGUID<T>(value: T): T {
	if (!value) return value;
	if (typeof value !== 'object') return value;
	if (value instanceof Binary && value.sub_type === Binary.SUBTYPE_UUID) return value.toString('hex') as T;

	if (Array.isArray(value)) return value.map(normalizeGUID) as T;

	if (isObject(value)) {
		const result: Record<string, unknown> = {};

		Object.entries(value).forEach(([key, val]) => {
			result[key] = normalizeGUID(val);
		});

		return result as T;
	}

	return value;
}

export function deserializeWithGUID<T>(buffer: Uint8Array): T {
	try {
		const data = deserialize(buffer) as T;

		return normalizeGUID(data) as T;
	} catch (error) {
		throw new Error('Failed to parse GUID response');
	}
}

function buildHeaders<TUser extends BaseUser, TApplicationState extends BaseApplicationState<TUser>>(
	state?: TApplicationState,
): HeadersInit {
	const headers: HeadersInit = {
		'Content-Type': `${ApplicationMediaType.json}; charset=utf-8`,
		'X-Client-Type': '0',
		'X-Application-Version': globals.version,
	};

	if (globals.features.useBson === '1') {
		headers.Accept = ApplicationMediaType.bson;
	}

	if (state?.login?.session) {
		headers.Cookie = `session=${state.login.session}`;
	}

	return headers;
}

function parse<T>(response: Response): Promise<Message<T | ResponseErrorContent>> {
	const contentType = getContentType(response);

	if (contentType.includes(ApplicationMediaType.bson)) {
		return response.arrayBuffer()
			.then((buffer) => {
				const uint8 = new Uint8Array(buffer);

				return deserializeWithGUID<Message<T | ResponseErrorContent>>(uint8);
			});
	}

	if (contentType.includes(ApplicationMediaType.json)) return response.json() as Promise<Message<T | ResponseErrorContent>>;

	throw new Error(`Unsupported content-type: ${contentType}`);
}

/**
 * This is the base of request - simple fetch to server to dedicated endpoint(data).
 * The result is converted to RequestError (upon failure) or item of requested entity T.
 * @param {string} type - endpoint request is intended to be made to (Actor name with word 'Actor' omitted, spelled with camelCase)
 * @param {BaseParams} data - request params (actor message data)
 * @param {TApplicationState} state - current state (is required for login data)
 * @param signal
 * @param idempotencyToken
 * @returns {Promise<T>} promise that is resolved with requested data or rejected with RequestError object
 */

export async function request<
	T,
	TUser extends BaseUser,
	TApplicationState extends BaseApplicationState<TUser>
>(type: string, data: BaseParams = {}, state?: TApplicationState, signal?: AbortSignal, idempotencyToken?: string): Promise<T> {
	const headers = buildHeaders(state);
	const payload = {
		type,
		data: JSON.stringify(data),
		idempotencyToken,
	};

	return fetch('api/post', {
		signal,
		credentials: 'same-origin',
		method: 'POST',
		headers,
		body: JSON.stringify(payload),
	})
		.then((response: Response) => {
			if (!response.ok) throw new Error('Failed to fetch');

			return parse<T | ResponseErrorContent>(response);
		})
		.then((message: Message<T | ResponseErrorContent>) => {
			if (message.success) return message.response as T;

			throw new ResponseError(message.response as ResponseErrorContent);
		});
}
